import { injectBetData } from "@fresh8/ad-data-composition/src/betData";
import * as generateLinks from "@fresh8/ad-data-composition/src/lib/generateLinks";
import {
  injectAdTag,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { computed, ComputedRef } from "vue";
import { injectDisplayEvents } from "../composition/displayEvents";
import { injectPreview } from "@fresh8/ad-data-composition/src/preview";
import { Offer } from "@fresh8/types/src/betData";

export function useShouldDisplayOffer(): ComputedRef<boolean> {
  const betData = injectBetData();
  const brand = injectBrand();
  const adTag = injectAdTag();
  const previewContext = injectPreview();

  const displayOffer = computed(() => {
    // Allow the preview to turn off the offer
    if (
      previewContext?.preview.value.isPreview &&
      previewContext?.preview.value.hideOffer
    ) {
      return false;
    }

    // Override for previewing the offer for an operator on brands page.
    if (
      previewContext?.preview.value.useBrandOffer &&
      adTag?.operator.value &&
      brand?.offers.value?.[adTag.operator.value?._id || ""]
    ) {
      return true;
    }

    // Uses offer in bet payload as the top most priority (defined by a campaign)
    if (betData?.showOffer.value) {
      return true;
      // Check to see if we can use the brand based offer for the opertor
    } else {
      // Has an offer been defiend for this opertor
      if (
        adTag &&
        adTag.operator.value &&
        brand?.operatorHasOffer(adTag.operator.value?._id || "")
      ) {
        // Are there no markets and should there a fallback offer be shown
        if (
          (!betData?.markets?.value ||
            Object.keys(betData.markets.value).length === 0) &&
          brand?.adTemplate.value.displayOffersAsFallback
        ) {
          return true;
        }

        // Are offers enabled for this brand
        if (brand?.adTemplate.value.displayOfferCard) {
          return true;
        }
      }
    }
    return false;
  });

  return displayOffer;
}

export function useOfferCardDisplayState(): {
  offerDisplayedAsFallback: ComputedRef<boolean>;
  defaultOfferDisplayedAsFirstCard: ComputedRef<boolean>;
} {
  /**
   * This checks to see if the offer was displayed as a fallback or not
   * used for the anayltics reporting
   */
  const offerDisplayedAsFallback = computed(() => {
    const brand = injectBrand();
    const adTag = injectAdTag();

    const displayEvents = injectDisplayEvents();
    // Has an offer been defiend for this opertor
    if (
      adTag &&
      adTag.operator.value &&
      brand?.operatorHasOffer(adTag.operator.value?._id || "")
    ) {
      // Are there no markets and should there a fallback offer be shown
      if (
        (!displayEvents ||
          displayEvents.flattendOrderedEvents?.value?.length === 0) &&
        brand?.adTemplate.value.displayOffersAsFallback
      ) {
        return true;
      }
    }

    return false;
  });

  /**
   * This checks to see if the default offer was displayed.
   * used for the anayltics reporting
   */
  const defaultOfferDisplayedAsFirstCard = computed(() => {
    const betData = injectBetData();
    const brand = injectBrand();
    const adTag = injectAdTag();
    const displayEvents = injectDisplayEvents();
    // If there's no bets then the fallback offer will be used
    if (displayEvents?.flattendOrderedEvents?.value?.length === 0) {
      return false;
    }

    // Uses offer in bet payload as the top most priority (defined by a campaign)
    if (betData?.showOffer.value) {
      return false;
    }

    // Has an offer been defiend for this opertor
    if (
      adTag &&
      adTag.operator.value &&
      brand?.operatorHasOffer(adTag.operator.value?._id || "")
    ) {
      // Are offers enabled for this brand
      if (brand?.adTemplate.value.displayOfferCard) {
        return true;
      }
    }
    return false;
  });

  return {
    offerDisplayedAsFallback,
    defaultOfferDisplayedAsFirstCard
  };
}

export function useGetOffer(): ComputedRef<false | Offer | undefined> {
  const betData = injectBetData();
  const brand = injectBrand();
  const adTag = injectAdTag();
  const previewContext = injectPreview();

  const brandOffer = computed(() => {
    const operatorOffer =
      adTag &&
      brand &&
      adTag.operator.value &&
      brand.operatorHasOffer(adTag.operator.value?._id || "") &&
      brand.offers.value?.[adTag.operator.value?._id || ""];

    return (
      adTag &&
      operatorOffer &&
      generateLinks.offer(operatorOffer, {
        affiliateId: betData?.affiliateId.value,
        clickUrl: betData?.clickUrl.value || "",
        operatorKey: adTag.operator.value.key
      })
    );
  });

  const displayOffer = computed(() => {
    // Override for previewing the offer for an operator on brands page.
    if (
      adTag &&
      previewContext?.preview.value.useBrandOffer &&
      brand?.offers.value?.[adTag.operator.value?._id || ""]
    ) {
      return brandOffer.value;
    }
    // Uses offer in bet payload as the top most priority (defined by a campaign)
    if (betData?.showOffer.value) {
      return betData.offer.value;
      // Check to see if we can use the brand based offer for the opertor
    } else {
      // Has an offer been defiend for this opertor
      return brandOffer.value;
    }
  });

  return displayOffer;
}
